const PHONE_COUNTRY_CODE = '+1';
export const PHONE_PREFIX = `${PHONE_COUNTRY_CODE} `;
const PHONE_PREFIX_DIGIT_COUNT = 1;
export const PHONE_DIGIT_COUNT = 10;
export const PHONE_MAX_LENGTH = 17; // +1 (XXX) XXX-XXXX
export const PHONE_REGEX = /^\d{10}$/;
export const PHONE_REGEX_WITH_EXCHANGE_CODE = /^[2-9][0-9]{2}$/;
const PHONE_REGEX_WITH_AREA_CODE = /^(?!988|[2-9]11)[2-9][0-9]{2}$/;
export const PHONE_ALLOWED_CHARACTERS_REGEX = /^[\d+\s().-]+$/;
const PHONE_AREA_CODE_START_REGEX = /^[2-9]/;

export const stripNonDigits = (value: string): string =>
  value.replace(/\D/g, '');

export const hasExplicitSupportedPhoneCountryCode = (value: string): boolean =>
  value.trim().startsWith(PHONE_COUNTRY_CODE);

export const hasUnsupportedPhoneCountryCode = (value: string): boolean => {
  const trimmedValue = value.trim();
  return (
    trimmedValue.startsWith('+') && !trimmedValue.startsWith(PHONE_COUNTRY_CODE)
  );
};

export const getLocalPhoneDigits = (value: string): string => {
  const digits = stripNonDigits(value);

  if (hasExplicitSupportedPhoneCountryCode(value)) {
    return digits.slice(PHONE_PREFIX_DIGIT_COUNT);
  }

  return digits;
};

export const hasInvalidPhoneAreaCode = (value: string): boolean => {
  const localDigits = getLocalPhoneDigits(value);
  const areaCode = localDigits.slice(0, 3);

  return (
    (localDigits.length > 0 &&
      !PHONE_AREA_CODE_START_REGEX.test(localDigits)) ||
    (areaCode.length === 3 && !PHONE_REGEX_WITH_AREA_CODE.test(areaCode))
  );
};

const getNormalizedPhoneDigits = (value: string): string => {
  if (hasUnsupportedPhoneCountryCode(value)) {
    return '';
  }

  const digits = stripNonDigits(value);

  if (digits.length === 0) {
    return '';
  }

  if (hasExplicitSupportedPhoneCountryCode(value)) {
    return getLocalPhoneDigits(value).slice(0, PHONE_DIGIT_COUNT);
  }

  if (digits.length === PHONE_DIGIT_COUNT + 1 && digits.startsWith('1')) {
    return digits.slice(1);
  }

  return digits.slice(0, PHONE_DIGIT_COUNT);
};

const formatLocalPhoneNumber = (text: string): string => {
  let formattedText = text.replace(/[^\d+]/g, '');
  if (!formattedText) {
    return '';
  }
  if (formattedText.startsWith('+') && !formattedText.startsWith('+1')) {
    return formattedText;
  }
  if (formattedText.startsWith('+1')) {
    formattedText = formattedText.slice(2);
  }

  if (formattedText.length > 3 && formattedText.length <= 5) {
    return `(${formattedText.slice(0, 3)}) ${formattedText.slice(3)}`;
  }
  if (formattedText.length > 6) {
    return `(${formattedText.slice(0, 3)}) ${formattedText.slice(3, 6)}-${formattedText.slice(6, 10)}`;
  }
  return formattedText;
};

export const formatPhoneNumber = (
  text: string,
  keepCountryCode: boolean = true,
): string => {
  if (!keepCountryCode) {
    return formatLocalPhoneNumber(text);
  }

  const digits = getNormalizedPhoneDigits(text);

  if (digits.length === 0) {
    return '';
  }

  if (digits.length <= 3) {
    return `${PHONE_PREFIX}(${digits}`;
  }

  if (digits.length <= 6) {
    return `${PHONE_PREFIX}(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }

  return `${PHONE_PREFIX}(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, PHONE_DIGIT_COUNT)}`;
};

export const formatPhoneInputText = (text: string): string => {
  const trimmedText = text.trim();

  if (
    text.length === 0 ||
    trimmedText === '+' ||
    trimmedText === PHONE_COUNTRY_CODE ||
    (hasExplicitSupportedPhoneCountryCode(trimmedText) &&
      getLocalPhoneDigits(trimmedText).length === 0)
  ) {
    return PHONE_PREFIX;
  }

  return formatPhoneNumber(text);
};

export const formatPhoneNumberWithoutPrefix = (phoneNumber: string): string => {
  if (phoneNumber) {
    return phoneNumber.replace('+1', '');
  }
  return phoneNumber;
};

export const formatDefaultPhoneNumber = (number: string) => {
  const cleanNumber = number.trim().replace(/[^\d+]/g, '');

  return cleanNumber.replace(/^(\+\d)(\d{3})(\d{3})(\d{4})$/, '$1 ($2) $3-$4');
};

export const clearPhoneNumberFormatting = (
  phoneNumber: string,
  countryCode: string = '+1',
  keepCountryCode: boolean = true,
) => {
  const validPhoneNumber = phoneNumber
    .replace(countryCode, '')
    .replaceAll(' ', '')
    .replace('(', '')
    .replace(')', '')
    .replace('-', '');
  if (keepCountryCode) {
    return countryCode + validPhoneNumber;
  }
  return validPhoneNumber;
};
